import { ObservableStore } from '@codewithdan/observable-store';
import { filter } from 'rxjs/operators';
import { v4 } from 'uuid';
import templates from '../../data/templates';
import { Store as MKCStore } from '../types';
import { initialStore, storage_key } from './_defaults';
import { convertParts, sanitizeV2ToV3, sanitizeV3ToV4 } from './_sanitizations';
import Draft from './Draft';
import Editor from './Editor';
import Kites from './Kites';
import Session from './Session';

export class Store extends ObservableStore<MKCStore> {
    stateChanged = this.stateChanged.pipe(filter((state) => !!state));
    getStore = () => this.getState();

    constructor() {
        super({});

        // init local storage
        if (!localStorage.getItem(storage_key)) {
            const initialKiteId = v4();
            const initialKite = {
                ...initialStore.draft,
                id: initialKiteId,
            };
            localStorage.setItem(
                storage_key,
                JSON.stringify({
                    ...initialStore,
                    draft: initialKite,
                    kites: {
                        [initialKiteId]: initialKite,
                    },
                })
            );
        }

        let storageState = JSON.parse(
            localStorage.getItem(storage_key)
        ) as MKCStore;

        // sanitize
        const { version } = storageState.meta || { version: null };

        let nextKites = storageState.kites;

        // v1 sanitize parts with part type in favour of separate objects
        if (!version) {
            console.info('MCK Store: v.null sanitizing kite parts');
            nextKites = convertParts(nextKites);

            storageState.meta = {
                version: 1,
            };
        }

        // v2 sanitize id property
        // todo refactor as utility
        if (!version || version < 2) {
            console.info('MCK Store: v.1 sanitizing kite ids');
            nextKites = Object.entries(nextKites).reduce(
                (sanitizedKites: MKCStore['kites'], [key, kite]) => {
                    return kite.id
                        ? {
                              ...sanitizedKites,
                              [key]: kite,
                          }
                        : {
                              ...sanitizedKites,
                              [key]: {
                                  ...kite,
                                  id: key,
                              },
                          };
                },
                {}
            );

            storageState.meta = {
                version: 2,
            };
        }

        // todo cleanup: before v3 sanitizing was concerned about kites only ...
        storageState = {
            ...storageState,
            kites: nextKites,
        };

        // v3 colors out, swatches in
        storageState = sanitizeV2ToV3(storageState);

        // v4 kite meta
        storageState = sanitizeV3ToV4(storageState);

        // todo : see above
        nextKites = storageState.kites;

        // recheck defaults
        if (!storageState.draft || !Object.keys(storageState.draft).length) {
            storageState.draft = Object.keys(nextKites).length
                ? nextKites[Object.keys(nextKites)[0]]
                : { ...initialStore.draft };
        }

        if (storageState.editor) {
            storageState.editor.draftSaved = true;
            if (!storageState.editor.material) {
                storageState.editor.material = 'icarexPc31';
            }
        } else {
            storageState.editor = { ...initialStore.editor };
        }

        console.info(
            `MKC Store v.${storageState.meta.version} | May the breeze be with you`
        );

        this.setState({
            ...storageState,
            kites: nextKites,
        });

        // -- setup draft from search params
        const create = new URL(location.href).searchParams.get('create');
        if (create && templates[create]) {
            Kites.addKite(create);
            history.replaceState(null, null, '/');
        }

        this.globalStateChanged.subscribe((store) => {
            localStorage.setItem(storage_key, JSON.stringify(store));
        });
    }
}

export default new Store();
export { Session, Draft, Kites, Editor };
